import { useRef, useState } from 'react';
import { IMAGE_MAX_BYTES, IMAGE_MIME_TYPES, type MapObject } from '@etown/shared';
import { Modal } from '../../components/Modal';
import { api } from '../../net/api';
import { useObjectImagesStore } from '../../stores/objectImagesStore';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

/**
 * Bild an der Wand: gross dargestellt, wenn man davorsteht. Wer direkt davor
 * steht, kann per Upload ein neues Bild setzen (Server prueft Naehe, Typ und
 * Groesse); das Ergebnis wird an alle in der Map gebroadcastet.
 */
export function PictureModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { mapId } = useOffice();
  const imageId = useObjectImagesStore((s) => s.images[object.id]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = ''; // erneutes Auswaehlen derselben Datei erlauben
    if (!file) return;
    setError(null);
    if (!(IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) {
      setError('Nur PNG, JPEG oder WebP erlaubt.');
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      setError('Das Bild ist zu gross (max. 1,5 MB).');
      return;
    }
    setUploading(true);
    try {
      // object:image-Broadcast aktualisiert den Store fuer alle (inkl. uns)
      await api.uploadObjectImage(mapId, object.id, file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const label = object.props.label ?? 'Bild';

  return (
    <Modal title={label} onClose={onClose} wide>
      <div className="picture-modal">
        {imageId ? (
          <img className="picture-modal__image" src={`/api/images/${encodeURIComponent(imageId)}`} alt={label} />
        ) : (
          <p className="picture-modal__empty">Noch kein Bild — lade eines hoch.</p>
        )}
        {error && <p className="picture-modal__error" role="alert">{error}</p>}
        <div className="picture-modal__actions">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => void onFile(e)}
          />
          <button
            className="picture-modal__upload"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Lädt hoch…' : imageId ? 'Bild wechseln' : 'Bild hochladen'}
          </button>
          <span className="picture-modal__hint">PNG, JPEG oder WebP · max. 1,5 MB</span>
        </div>
      </div>
    </Modal>
  );
}
